import React, { useEffect, useState } from "react";
import "../../css/ProductCreateScreen.css";
import { getDatabase, ref, onValue, update, off } from "firebase/database";
import {
	getStorage,
	ref as storageRef,
	uploadBytes,
	getDownloadURL,
	deleteObject,
} from "firebase/storage";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import LoaderComponent from "../../component/LoaderComponent";
import UploadFileToDatabase from "../../component/UploadFileToDatabase";
import { useAdminAuth } from "../../component/AdminUserPresent";

const ProductEditScreen = () => {
	const { category, productId } = useParams();
	const navigate = useNavigate();
	const db = getDatabase();

	const [productTitle, setProductTitle] = useState("");
	const [productDescription, setProductDescription] = useState("");
	const [productCategory, setProductCategory] = useState(category);
	const [existingImages, setExistingImages] = useState([]);
	const [newFiles, setNewFiles] = useState([]);
	const [loader, setLoader] = useState(false);
	const isAdmin = useAdminAuth();

	// 🔹 Fetch product
	useEffect(() => {
		if (!isAdmin) return;
		setLoader(true);
		const productRef = ref(db, `productDetails/${category}/${productId}`);
		onValue(productRef, (snapshot) => {
			const data = snapshot.val();
			if (data) {
				setProductTitle(data.productTitle);
				setProductDescription(data.productDescription);
				setProductCategory(data.productCategory);
				setExistingImages(data.postImageArray || []);
			}
			setLoader(false);
		});

		return () => off(productRef);
	}, [category, productId]);

	// 🔹 Update product (basic)
	const handleUpdateProduct = async () => {
		if (!productTitle || !productDescription) {
			toast.warn("All fields are required");
			return;
		}

		try {
			setLoader(true);
			const storage = getStorage();
			const db = getDatabase();

			let finalImages = existingImages; // default: keep existing images

			// ✅ Only if new images are selected
			if (newFiles.length > 0) {
				// 1️⃣ Delete existing images from Firebase Storage
				for (let img of existingImages) {
					if (!img.url) continue;
					try {
						// Extract storage path from URL
						const path = decodeURIComponent(
							img.url.split("/o/")[1].split("?")[0],
						);
						const imgRef = storageRef(storage, path);
						await deleteObject(imgRef);
					} catch (err) {
						console.log("Image already deleted or error:", err);
					}
				}

				// 2️⃣ Upload new images
				const uploadedImages = [];
				for (let i = 0; i < newFiles.length; i++) {
					const file = newFiles[i].file || newFiles[i];
					const imgRef = storageRef(
						storage,
						`productImages/${productId}_${i}`,
					);
					await uploadBytes(imgRef, file);
					const url = await getDownloadURL(imgRef);

					uploadedImages.push({
						name: file.name,
						type: "image",
						url,
					});
				}

				finalImages = uploadedImages; // only new images
			}

			// 3️⃣ Update database (text fields + images)
			await update(
				ref(db, `productDetails/${productCategory}/${productId}`),
				{
					productTitle,
					productDescription,
					productCategory,
					postImageArray: finalImages,
					productId,
					updatedDate: Date(),
				},
			);

			toast.success("Product updated successfully!");
			navigate("/productlisted");
		} catch (error) {
			console.error(error);
			toast.error("Update failed");
		} finally {
			setLoader(false);
		}
	};

	return (
		<div className="container">
			{loader && <LoaderComponent />}

			<div className="text-center product-create-screen-header py-4">
				Edit Product
			</div>

			<div className="container product-create-layout">
				<div className="row product-create-view">
					<div className="col-lg-6 col-12 mx-auto my-2">
						<div className="input-field-div">
							<label>Product Title</label>
							<input
								value={productTitle}
								onChange={(e) =>
									setProductTitle(e.target.value)
								}
							/>
						</div>
					</div>

					<div className="col-lg-6 col-12 mx-auto my-2">
						<div className="input-field-div">
							<label>Product Description</label>
							<textarea
								value={productDescription}
								onChange={(e) =>
									setProductDescription(e.target.value)
								}
							/>
						</div>
					</div>

					<div className="col-lg-6 col-12 mx-auto my-2">
						<div className="input-field-div">
							<label>Product Category</label>
							<select
								value={productCategory}
								onChange={(e) =>
									setProductCategory(e.target.value)
								}
							>
								<option>hand-tufted-rugs</option>
								<option>hand-knotted-rugs</option>
								<option>flatweave-kilim-rugs</option>
								<option>hand-woven-rugs</option>
								<option>custom-loom-rugs</option>
								<option>machine-made-rugs</option>
							</select>
						</div>
					</div>

					{/* 🔹 Existing images */}
					<div className="col-6 text-center my-3">
						<h4>Existing Images</h4>
						<div className="d-flex justify-content-center pt-2 flex-wrap">
							{existingImages.map((img, index) => (
								<img
									key={index}
									src={img.url}
									alt={img.name}
									className="product-thumb mx-2"
								/>
							))}
						</div>
					</div>

					{/* 🔹 Upload new images */}
					<div className="col-lg-6 col-12 mx-auto my-2 pt-3 product-image text-center">
						<UploadFileToDatabase
							files={newFiles}
							setFiles={setNewFiles}
							progress={0}
						/>
					</div>
				</div>

				<div className="text-center py-4">
					<button
						className="submit-details"
						onClick={handleUpdateProduct}
					>
						Update Product
					</button>
				</div>
			</div>
		</div>
	);
};

export default ProductEditScreen;
